import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, DatePicker, Button, Table, Spin, Typography, Space, Statistic, InputNumber } from 'antd';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import dayjs from 'dayjs';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Layout from '../../layout/Layout';
import { getHeaders } from '../../utility/getHeader';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const currencyFormatter = new Intl.NumberFormat('th-TH', {
  style: 'currency',
  currency: 'THB',
  minimumFractionDigits: 2
});

const SponsorReports = () => {
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [sponsorData, setSponsorData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [grandTotal, setGrandTotal] = useState(0);
  const [percentMap, setPercentMap] = useState({});

  const totalAmount = useMemo(() => {
    return sponsorData.reduce((acc, item) => acc + Number(item.total_value || item.total_purchase_amount || item.total_amount || 0), 0);
  }, [sponsorData]);

  const handlePercentChange = (key, value) => {
    setPercentMap((prev) => ({ ...prev, [key]: value ?? 0 }));
  };

  const fetchSponsorData = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      toast.warning('Please select a date range');
      return;
    }

    try {
      setLoading(true);
      const from = dateRange[0].format('YYYY-MM-DD');
      const to = dateRange[1].format('YYYY-MM-DD');
      const response = await axios.get('/warehousemanager/sponsor-distribution', {
        params: { from, to },
        ...getHeaders()
      });

      const rows = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];

      // Map the response data to match expected fields
      const mappedData = rows.map(item => ({
        ...item,
        total_purchase_amount: item.total_value || item.total_purchase_amount || 0,
        total_amount: item.total_value || item.total_amount || 0
      }));

      setSponsorData(mappedData);
      
      // Calculate grand total from the data
      const total = mappedData.reduce((sum, item) => sum + Number(item.total_value || 0), 0);
      setGrandTotal(total);
      
      toast.success('Sponsor data loaded successfully');
    } catch (error) {
      console.error('Error fetching sponsor report:', error);
      toast.error('Failed to load sponsor report');
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    if (!sponsorData || sponsorData.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();

    // Set font
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(31, 75, 153);
    doc.text('Sponsor Purchase Report', pageWidth / 2, 15, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100);
    const dateRangeText = `Period: ${dateRange[0].format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}`;
    doc.text(dateRangeText, pageWidth / 2, 22, { align: 'center' });

    // Prepare table data with THB suffix
    const tableData = sponsorData.map((row, index) => {
      const key = row.sponsor_name || `row-${index}`;
      const pct = percentMap[key] ?? 0;
      const base = Number(row.total_value || row.total_purchase_amount || row.total_amount || 0);
      const computed = (base * (Number(pct) || 0)) / 100;
      
      return [
        index + 1,
        row.sponsor_name || '—',
        row.total_orders || 0,
        row.unique_items || 0,
        Number(row.total_quantity || 0).toLocaleString(),
        Number(row.total_sold || 0).toLocaleString(),
        Number(row.current_stock || 0).toLocaleString(),
        base.toFixed(2) + ' THB',
        pct.toFixed(2) + '%',
        computed.toFixed(2) + ' THB'
      ];
    });

    // Calculate total percent amount
    const totalPercentAmount = sponsorData.reduce((sum, row, index) => {
      const key = row.sponsor_name || `row-${index}`;
      const pct = percentMap[key] ?? 0;
      const base = Number(row.total_value || row.total_purchase_amount || row.total_amount || 0);
      const computed = (base * (Number(pct) || 0)) / 100;
      return sum + computed;
    }, 0);

    doc.autoTable({
      startY: 28,
      head: [['S.No', 'Sponsor', 'Orders', 'Items', 'Total Qty', 'Sold', 'Stock', 'Value', '%', 'Percent Amt']],
      body: tableData,
      foot: [[
        { content: 'Total', colSpan: 7, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: (grandTotal || totalAmount).toFixed(2) + ' THB', styles: { fontStyle: 'bold' } },
        '',
        { content: totalPercentAmount.toFixed(2) + ' THB', styles: { fontStyle: 'bold' } }
      ]],
      headStyles: {
        fillColor: [30, 58, 138],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold',
        font: 'helvetica'
      },
      footStyles: {
        fillColor: [249, 250, 251],
        textColor: [0, 0, 0],
        fontSize: 10,
        fontStyle: 'bold',
        font: 'helvetica'
      },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      styles: { 
        fontSize: 9, 
        cellPadding: 3,
        font: 'helvetica'
      },
      margin: { left: 10, right: 10 }
    });

    // Add footer with generation info
    const finalY = doc.lastAutoTable.finalY + 15;
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerY = pageHeight - 20;
    
    // Get user info
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo') || '{}');
    const userName = userInfo.username || userInfo.user_name || userInfo.userId || 'Admin';
    const generatedDate = dayjs().format('DD/MM/YYYY hh:mm A');
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated by: ${userName}`, 15, footerY);
    doc.text(`Date & Time: ${generatedDate}`, 15, footerY + 5);
    doc.text(`Report Period: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`, 15, footerY + 10);

    doc.save(`Sponsor_Report_${dayjs().format('YYYY-MM-DD')}.pdf`);
    toast.success('PDF exported successfully');
  };

  const exportToExcel = () => {
    if (!sponsorData || sponsorData.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const excelData = sponsorData.map((row, index) => {
      const key = row.sponsor_name || `row-${index}`;
      const pct = percentMap[key] ?? 0;
      const base = Number(row.total_value || row.total_purchase_amount || row.total_amount || 0);
      const computed = (base * (Number(pct) || 0)) / 100;
      
      return {
        'S.No': index + 1,
        'Sponsor Name': row.sponsor_name || '—',
        'Total Orders': row.total_orders || 0,
        'Unique Items': row.unique_items || 0,
        'Total Quantity': row.total_quantity || 0,
        'Sold Quantity': row.total_sold || 0,
        'Current Stock': row.current_stock || 0,
        'Total Value (THB)': base.toFixed(2),
        'Percent %': pct.toFixed(2),
        'Percent Amount (THB)': computed.toFixed(2)
      };
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sponsor Report');
    XLSX.writeFile(wb, `Sponsor_Report_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    toast.success('Excel exported successfully');
  };

  useEffect(() => {
    fetchSponsorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: 'S.No',
      key: 'serial',
      width: 70,
      align: 'center',
      render: (_value, _record, index) => index + 1
    },
    {
      title: 'Sponsor Name',
      dataIndex: 'sponsor_name',
      key: 'sponsor_name',
      render: (value) => value || '—'
    },
    {
      title: 'Orders',
      dataIndex: 'total_orders',
      key: 'total_orders',
      align: 'center',
      render: (value) => value || 0
    },
    {
      title: 'Items',
      dataIndex: 'unique_items',
      key: 'unique_items',
      align: 'center',
      render: (value) => value || 0
    },
    {
      title: 'Total Qty',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      align: 'right',
      render: (value) => Number(value || 0).toLocaleString()
    },
    {
      title: 'Sold Qty',
      dataIndex: 'total_sold',
      key: 'total_sold',
      align: 'right',
      render: (value) => Number(value || 0).toLocaleString()
    },
    {
      title: 'Stock',
      dataIndex: 'current_stock',
      key: 'current_stock',
      align: 'right',
      render: (value) => Number(value || 0).toLocaleString()
    },
    {
      title: 'Total Value',
      dataIndex: 'total_value',
      key: 'total_value',
      align: 'right',
      render: (value) => currencyFormatter.format(Number(value || 0))
    },
    {
      title: 'Percent %',
      key: 'percent',
      align: 'center',
      width: 120,
      render: (_value, record, index) => {
        const key = record.sponsor_name || `row-${index}`;
        const val = percentMap[key] ?? 0;
        return (
          <InputNumber
            min={0}
            max={100}
            value={val}
            onChange={(v) => handlePercentChange(key, v)}
            size="small"
            style={{ width: 90 }}
          />
        );
      }
    },
    {
      title: 'Percent Amount',
      key: 'percentAmount',
      align: 'right',
      render: (_value, record, index) => {
        const key = record.sponsor_name || `row-${index}`;
        const pct = percentMap[key] ?? 0;
        const base = Number(record.total_value || record.total_purchase_amount || record.total_amount || 0);
        const computed = (base * (Number(pct) || 0)) / 100;
        return currencyFormatter.format(computed);
      }
    }
  ];

  return (
    <Layout>
      <div className="container-fluid py-3">
        <ToastContainer />
        <Card className="mb-3" bodyStyle={{ padding: '16px 16px 8px 16px' }}>
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col>
              <div>
                <Title level={4} style={{ marginBottom: 4, color: '#1f4b99' }}>Sponsor Reports</Title>
                <Text type="secondary">View sponsor totals from warehouse stock (wstock) by date range</Text>
              </div>
            </Col>
            <Col>
              <Space size={8} wrap>
                <RangePicker
                  allowClear={false}
                  value={dateRange}
                  onChange={(values) => setDateRange(values)}
                  disabledDate={(current) => current && current > dayjs()}
                  format="YYYY-MM-DD"
                  size="middle"
                />
                <Button type="primary" onClick={fetchSponsorData}>Fetch</Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]} className="mb-3">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card bordered={false} style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}>
              <Statistic
                title="Sponsors"
                value={sponsorData.length}
                valueStyle={{ fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card bordered={false} style={{ background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)' }}>
              <Statistic
                title="Total Amount"
                value={currencyFormatter.format(grandTotal || totalAmount)}
                valueStyle={{ fontWeight: 700 }}
              />
            </Card>
          </Col>
        </Row>

        <div className="mb-3 d-flex justify-content-end gap-2">
          <Button 
            type="primary" 
            danger
            onClick={exportToPDF}
            disabled={!sponsorData || sponsorData.length === 0}
          >
            Export PDF
          </Button>
          <Button 
            type="primary"
            style={{ backgroundColor: '#10b981' }}
            onClick={exportToExcel}
            disabled={!sponsorData || sponsorData.length === 0}
          >
            Export Excel
          </Button>
        </div>

        <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Spin spinning={loading} tip="Loading sponsor data...">
            <Table
              rowKey={(record, index) => record.sponsor_id || record.sponsor_name || index}
              columns={columns}
              dataSource={sponsorData}
              pagination={{ pageSize: 10, showSizeChanger: true, size: 'small' }}
              locale={{ emptyText: 'No sponsor data found' }}
              size="small"
              rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
              className="sponsor-table"
            />
          </Spin>
        </Card>
        <style>{`
          .sponsor-table .ant-table-thead > tr > th {
            background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%) !important;
            color: white !important;
            font-weight: 600 !important;
            font-size: 13px !important;
            padding: 10px 12px !important;
            border-bottom: none !important;
            text-align: center !important;
          }
          .sponsor-table .ant-table-tbody > tr > td {
            padding: 8px 12px !important;
            font-size: 13px !important;
            border-bottom: 1px solid #e5e7eb !important;
          }
          .sponsor-table .table-row-light {
            background-color: #f9fafb !important;
          }
          .sponsor-table .table-row-dark {
            background-color: #ffffff !important;
          }
          .sponsor-table .ant-table-tbody > tr:hover > td {
            background-color: #dbeafe !important;
            transition: all 0.3s ease;
          }
          .sponsor-table .ant-input-number {
            border-radius: 4px !important;
            border-color: #bfdbfe !important;
          }
          .sponsor-table .ant-input-number:focus,
          .sponsor-table .ant-input-number:hover {
            border-color: #3b82f6 !important;
          }
          .sponsor-table .ant-table {
            border-radius: 8px !important;
            overflow: hidden !important;
          }
        `}</style>
      </div>
    </Layout>
  );
};

export default SponsorReports;
